import { computed, type Ref, ref, watch } from "vue"
import {
  type AugerField,
  type Field,
  getFieldType,
  isJunctionField,
  isReferenceDef,
  isReferenceField,
  type SavedFilter,
  type SavedFilterCondition,
} from "@auger/core/fields"
import { OPERATORS } from "@auger/core/operators"
import { getNestedValue } from "@auger/ui/composables/useCollection"
// Deep import to avoid importing cycles and the entire field-component graph.
import { resolveTargets } from "@/common/fields/resolveTargets"
import type { ComposableDeps } from "./types"

// --- Types -----------------------------------------------------------------

export type Logic = NonNullable<SavedFilter["logic"]>

/**
 * A single filter condition as produced by useFilter and stored on records
 * for `filter`-type fields. `enabled: false` hides the condition from
 * evaluation without removing it from the user's workbench. The editor always
 * initializes id, value and enabled; core permits these to be absent when saved.
 */
export type FilterCondition = Required<SavedFilterCondition>

/**
 * The stored shape written to `record.data[field.key]` for a `filter` field,
 * identical to `useFilter().serialized.value`.
 */
export type SerializedFilter = Omit<SavedFilter, "conditions"> & {
  conditions: FilterCondition[]
}

/**
 * A field as the filter reads it: any part of a `Field` plus the `key` it is
 * addressed by.
 */
export interface FilterFieldDef extends Partial<Field> {
  key: string
  /** The caption of the field's trigger in the field picker. */
  triggerLabel?: string
}

/**
 * A filter field with Auger's record scoping (`recordType`) and reverse-link
 * metadata (`subtype`, `sourceType`), which the filter editors read to pick a value
 * input and a record type.
 */
export interface AugerFilterFieldDef
  extends FilterFieldDef, Partial<Pick<AugerField, "recordType" | "subtype" | "sourceType">> {}

/** A resolved target the filter chips render: an id, usually with a display name. */
export type FilterEntity = { id: number | string; name?: string; [k: string]: unknown }

// --- Field enrichment ------------------------------------------------------

function toIds(item: unknown): (number | string)[] {
  if (item == null) return []
  const arr = Array.isArray(item) ? item : [item]
  return arr.filter((v) => v != null) as (number | string)[]
}

/** The relationship a junction field targets (`links`/`taggings`/`attachments`),
 *  or null for anything else — the resolvable target tables. */
function junctionRelationship(field: FilterFieldDef | null | undefined): string | null {
  if (!field?.type) return null
  const def = getFieldType(field.type)
  // `def` is undefined for unregistered pseudo-types (filter reverse-link fields).
  const rel = def && isReferenceDef(def) ? def.relationship : undefined
  return rel === "links" || rel === "taggings" || rel === "attachments" ? rel : null
}

/** The filter's per-context `enrichField` (records.ts/listings.ts read a record;
 *  a filter has none). Junction fields resolve their chips off the bound id set
 *  (`options.value`) via `resolve`; other fields keep identity accessors. */
export function enrichFilterField(
  field: FilterFieldDef,
  options: {
    resolve?: (ids: (number | string)[]) => FilterEntity[]
    isArray?: boolean
    value?: unknown
  } = {}
): FilterFieldDef {
  const { resolve, isArray, value } = options
  const type = field.type
  const def = type ? getFieldType(type) : undefined
  const merged: FilterFieldDef = { ...field, isArray: isArray ?? field.isArray }

  const isChipReference =
    !!type &&
    !!def &&
    isReferenceDef(def) &&
    (isJunctionField({ type }) || isReferenceField({ type }))

  if (isChipReference && resolve) {
    // Arity-shaped: single-select yields one, array the set.
    const ids = toIds(value)
    merged.rawValue = () => (merged.isArray ? ids : (ids[0] ?? null))
    merged.value = () => {
      const entities = resolve(ids)
      return merged.isArray ? entities : (entities[0] ?? null)
    }
    return merged
  }

  merged.rawValue = () => value ?? null
  merged.value = () => value ?? null
  return merged
}

// --- Pure helpers ----------------------------------------------------------

/**
 * Build a human-readable summary of a filter.
 *
 * Pure and reactive-independent: safe to call from cells, previews, and
 * tests. Accepts either a full `SerializedFilter` or the looser
 * `{logic, conditions}` that `useFilter` hands to its own
 * `filterDescription` (where `conditions` is already enabled-filtered).
 *
 * Returns `""` for an empty, null, or undefined filter.
 *
 * @example
 *   describeFilter({ logic: "and", conditions: [...] }, fields)
 *   // => 'Name equals "Acme" AND Stage in "Open, Won"'
 */
export function describeFilter(
  serialized: Partial<SerializedFilter> | null | undefined,
  fields: FilterFieldDef[] = []
): string {
  const raw = Array.isArray(serialized?.conditions) ? serialized.conditions : []
  const active = raw.filter((c) => c.enabled !== false)
  if (!active.length) return ""

  const parts = active.map((c) => {
    const fieldDef = fields.find((f) => f.key === c.field)
    const fieldLabel = fieldDef?.label || c.field
    const operatorLabel = OPERATORS[c.operator]?.label || c.operator

    let valueStr = ""
    if (["isEmpty", "isNotEmpty", "isTrue", "isFalse"].includes(c.operator)) {
      valueStr = ""
    } else if (Array.isArray(c.value)) {
      const labels = c.value.map((v: unknown) =>
        typeof v === "object" && v !== null && "name" in v ? (v as { name: unknown }).name : v
      )
      valueStr = ` "${labels.join(", ")}"`
    } else if (c.value != null) {
      valueStr = ` "${c.value}"`
    }

    return `${fieldLabel} ${operatorLabel}${valueStr}`
  })

  return parts.join((serialized?.logic || "and") === "and" ? " AND " : " OR ")
}

// --- Composable ------------------------------------------------------------

/**
 * Composable for building filter functions from conditions.
 * Returns a computed `filterFn` consumers can apply to any collection via
 * `setFilter()`.
 *
 * @param fields - Field definitions (optionally carrying a `value()` extractor)
 * @param deps - `client` resolves the chips of link conditions
 */
export function useFilter<T extends FilterFieldDef>(fields: Ref<T[]>, deps: ComposableDeps) {
  const { client } = deps
  const conditions = ref<FilterCondition[]>([])
  const logic = ref<Logic>("and")

  // Record-less chip resolution: on any condition change, re-resolve each junction
  // condition's ids via resolveTargets (Apollo-cached) into `entities`, the
  // reactive lookup `enrich` reads.
  const entities = ref<Map<string, Map<number, FilterEntity>>>(new Map())
  let resolveGeneration = 0

  watch(
    conditions,
    async (conds) => {
      const generation = ++resolveGeneration
      const next = new Map<string, Map<number, FilterEntity>>()
      await Promise.all(
        conds.map(async (c) => {
          const field = fields.value.find((f) => f.key === c.field)
          const relationship = junctionRelationship(field)
          // Finite ids only — a polymorphic link's value can be free text (NaN).
          const ids = toIds(c.value).map(Number).filter(Number.isFinite)
          if (!relationship || !field?.type || !ids.length) return
          try {
            const resolved = await resolveTargets({ key: field.key, type: field.type }, ids, {
              client,
            })
            const map = next.get(relationship) ?? new Map<number, FilterEntity>()
            resolved.forEach((e) => map.set(Number(e.id), e as FilterEntity))
            next.set(relationship, map)
          } catch (err) {
            console.error("useFilter: failed to resolve filter targets", err)
          }
        })
      )
      // Ignore a slow earlier run that lands after a newer one.
      if (generation === resolveGeneration) entities.value = next
    },
    { immediate: true, deep: true }
  )

  /** Enrich a condition's field for the builder: junctions resolve chips off the
   *  hydrated `entities`; other fields pass through unchanged. */
  function enrich(
    field: FilterFieldDef | null | undefined,
    isArray: boolean,
    value: unknown
  ): FilterFieldDef | null {
    if (!field) return null
    const relationship = junctionRelationship(field)
    if (!relationship) return field
    return enrichFilterField(field, {
      isArray,
      value,
      resolve: (ids) => {
        const map = entities.value.get(relationship)
        if (!map) return []
        return toIds(ids)
          .map((id) => map.get(Number(id)))
          .filter((e): e is FilterEntity => !!e)
      },
    })
  }

  function generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
  }

  // --- Condition Management ---

  function addCondition({
    field,
    operator,
    value,
    enabled = true,
  }: {
    field: string
    operator: string
    value?: unknown
    enabled?: boolean
  }): string {
    const id = generateId()
    conditions.value = [...conditions.value, { id, field, operator, value, enabled }]
    return id
  }

  function updateCondition(id: string, updates: Partial<FilterCondition>): void {
    conditions.value = conditions.value.map((c) => (c.id === id ? { ...c, ...updates } : c))
  }

  function removeCondition(id: string): void {
    conditions.value = conditions.value.filter((c) => c.id !== id)
  }

  function toggleCondition(id: string): void {
    conditions.value = conditions.value.map((c) =>
      c.id === id ? { ...c, enabled: !c.enabled } : c
    )
  }

  function clearConditions(): void {
    conditions.value = []
  }

  function setLogic(mode: Logic): void {
    logic.value = mode
  }

  function setConditions(newConditions: Array<Partial<FilterCondition>>): void {
    conditions.value = newConditions.map((c) => ({
      id: c.id || generateId(),
      field: c.field ?? "",
      operator: c.operator ?? "",
      value: c.value,
      enabled: c.enabled ?? true,
    }))
  }

  // --- Filter Building ---

  function getFieldValue(item: unknown, fieldKey: string): unknown {
    const fieldDef = fields.value.find((f) => f.key === fieldKey)
    return fieldDef?.value ? fieldDef.value(item) : getNestedValue(item, fieldKey)
  }

  /**
   * Coerce a single value to the canonical representation for its field type.
   * Strings-from-inputs become numbers / timestamps / booleans, and Date
   * objects become timestamps, so the generic operator predicates (equals,
   * in, between, …) compare apples to apples.
   *
   * Other non-primitives (arrays, plain objects) pass through — relationship
   * operators (hasAny, hasAll) handle their own id-vs-object shape.
   */
  function coerceScalar(value: unknown, fieldType?: string): unknown {
    if (value == null) return value
    // Date instances → timestamp, regardless of field type (hydrated data
    // sometimes arrives as Date objects).
    if (value instanceof Date) return value.getTime()
    if (typeof value !== "string") return value
    if (fieldType === "number") {
      const n = Number(value)
      return Number.isNaN(n) ? value : n
    }
    if (fieldType === "date") {
      const t = new Date(value).getTime()
      return Number.isNaN(t) ? value : t
    }
    if (fieldType === "boolean") {
      if (value === "true") return true
      if (value === "false") return false
    }
    return value
  }

  /**
   * Coerce a condition value. Range / array shapes are element-wise because
   * each element is an independent bound/entry.
   */
  function coerceConditionValue(
    rawValue: unknown,
    fieldType: string | undefined,
    operator: string
  ): unknown {
    if (rawValue == null) return rawValue
    const shape = OPERATORS[operator]?.value
    if (shape === "range" || shape === "array") {
      if (!Array.isArray(rawValue)) return rawValue
      return rawValue.map((v) => coerceScalar(v, fieldType))
    }
    return coerceScalar(rawValue, fieldType)
  }

  function testCondition(item: unknown, condition: Partial<FilterCondition>): boolean {
    const { field, operator, value } = condition
    if (!operator) return true
    const operatorDef = OPERATORS[operator]
    if (!operatorDef) {
      console.warn(`Unknown operator: ${operator}`)
      return true
    }

    const fieldDef = fields.value.find((f) => f.key === field)
    const fieldType = fieldDef?.type
    const fieldValue = coerceScalar(getFieldValue(item, field ?? ""), fieldType)
    const conditionValue = coerceConditionValue(value, fieldType, operator)
    return operatorDef.fn(fieldValue, conditionValue)
  }

  // --- Computed ---

  const activeConditions = computed(() => conditions.value.filter((c) => c.enabled))
  const hasActiveFilters = computed(() => activeConditions.value.length > 0)

  const filterFn = computed(() => {
    const active = activeConditions.value
    if (active.length === 0) return null
    if (logic.value === "and") {
      return (item: unknown) => active.every((condition) => testCondition(item, condition))
    }
    return (item: unknown) => active.some((condition) => testCondition(item, condition))
  })

  const filterDescription = computed(() =>
    describeFilter({ logic: logic.value, conditions: activeConditions.value }, fields.value)
  )

  const serialized = computed<SerializedFilter>(() => ({
    logic: logic.value,
    conditions: conditions.value.map(({ id, field, operator, value, enabled }) => ({
      id,
      field,
      operator,
      value,
      enabled,
    })),
  }))

  /**
   * Load a serialized filter into the composable. Both `logic` and
   * `conditions` are applied when present; callers that need an explicit
   * "reset to defaults" should `clearConditions()` and `setLogic('and')`
   * themselves first — load is additive for partial payloads so a stored
   * value that has lost its `logic` key doesn't unexpectedly reset to 'and'.
   */
  function load(data: { logic?: Logic; conditions?: Array<Partial<FilterCondition>> }): void {
    if (data.logic) logic.value = data.logic
    if (data.conditions) setConditions(data.conditions)
  }

  return {
    // --- State ---
    conditions,
    logic,

    // --- Computed ---
    activeConditions,
    hasActiveFilters,
    filterFn,
    filterDescription,
    serialized,

    // --- Methods ---
    addCondition,
    updateCondition,
    removeCondition,
    toggleCondition,
    clearConditions,
    setConditions,
    setLogic,
    load,

    // --- Utilities ---
    testCondition,
    getFieldValue,
    enrich,
  }
}

export type UseFilterInstance = ReturnType<typeof useFilter>
